import { indexedDBManager } from "../utils/indexedDB";
import { ProductInterface } from "../interfaces/ProductInterface";
import { CategoryInterface } from "../interfaces/CategoryInterface";
import { digitalSalesApiSlice } from "../api/digital-sales";
import { store } from "../../app/store";
import { FilterListInterface } from "../interfaces/FilterListInterface";
import { ProductDetailsDataInterface } from "../interfaces/ProductDetailsDataInterface";
import { BrandingInterface } from "../interfaces/BrandingInterface";
import { BrandingTypeEnum } from "../enums/BrandingTypeEnum";

export interface SyncProgress {
    stage:
        | "initializing"
        | "fetching-products"
        | "fetching-categories"
        | "fetching-filters"
        | "fetching-branding"
        | "downloading-images"
        | "downloading-branding-files"
        | "completed"
        | "error";
    progress: number;
    total: number;
    message: string;
}

class DataSyncService {
    async syncAllData(
        onProgress?: (progress: SyncProgress) => void,
    ): Promise<void> {
        try {
            await indexedDBManager.init();

            onProgress?.({
                stage: "initializing",
                progress: 0,
                total: 100,
                message: "Initializing sync...",
            });

            onProgress?.({
                stage: "fetching-products",
                progress: 10,
                total: 100,
                message: "Fetching products...",
            });

            const allProducts = await this.fetchAllProducts();
            await this.syncProductsIncremental(allProducts);

            onProgress?.({
                stage: "fetching-categories",
                progress: 20,
                total: 100,
                message: "Fetching categories...",
            });

            const productIds = allProducts.map((product) => product.id);
            const categories = await this.fetchProductCategories(productIds);
            await this.syncCategoriesIncremental(categories);

            onProgress?.({
                stage: "fetching-filters",
                progress: 25,
                total: 100,
                message: "Fetching filters...",
            });

            const allFilters = await this.fetchAllFilters();
            await this.syncFiltersIncremental(allFilters);

            onProgress?.({
                stage: "fetching-branding",
                progress: 30,
                total: 100,
                message: "Fetching branding...",
            });

            const [brandingVideos, brandingPresentations] = await Promise.all([
                this.fetchBranding(BrandingTypeEnum.video),
                this.fetchBranding(BrandingTypeEnum.presentation),
            ]);
            const allBranding: BrandingInterface[] = [
                ...brandingVideos,
                ...brandingPresentations,
            ];
            await this.syncBrandingIncremental(allBranding);

            onProgress?.({
                stage: "downloading-images",
                progress: 40,
                total: 100,
                message: "Downloading images...",
            });

            await this.downloadAllImages(allProducts, onProgress);

            onProgress?.({
                stage: "downloading-branding-files",
                progress: 90,
                total: 100,
                message: "Downloading branding files...",
            });

            await this.downloadAllBrandingFiles(allBranding, onProgress);

            onProgress?.({
                stage: "completed",
                progress: 95,
                total: 100,
                message: "Finalizing sync...",
            });

            await indexedDBManager.setMetadata("lastSync", Date.now());

            onProgress?.({
                stage: "completed",
                progress: 100,
                total: 100,
                message: "Sync completed successfully!",
            });
        } catch (error) {
            onProgress?.({
                stage: "error",
                progress: 0,
                total: 100,
                message: `Sync failed: ${error instanceof Error ? error.message : "Unknown error"}`,
            });
            throw error;
        }
    }

    private async syncProductsIncremental(
        fetchedProducts: ProductInterface[],
    ): Promise<void> {
        const existingProducts = await indexedDBManager.getProducts();
        const fetchedProductIds = new Set(fetchedProducts.map((p) => p.id));

        const productsToDelete = existingProducts.filter(
            (p) => !fetchedProductIds.has(p.id),
        );

        const productsToUpdate: ProductInterface[] = [];
        for (const product of fetchedProducts) {
            const hasChanged =
                await indexedDBManager.hasProductChanged(product);
            if (hasChanged) {
                productsToUpdate.push(product);
            }
        }

        for (const product of productsToDelete) {
            await indexedDBManager.deleteProduct(product.id);
        }

        for (const product of productsToUpdate) {
            await indexedDBManager.storeProducts([product]);
        }
    }

    private async syncCategoriesIncremental(
        fetchedCategories: CategoryInterface[],
    ): Promise<void> {
        const existingCategories = await indexedDBManager.getCategories();
        const fetchedCategoryIds = new Set(fetchedCategories.map((c) => c.id));

        const categoriesToDelete = existingCategories.filter(
            (c) => !fetchedCategoryIds.has(c.id),
        );

        const categoriesToUpdate: CategoryInterface[] = [];
        for (const category of fetchedCategories) {
            const hasChanged =
                await indexedDBManager.hasCategoryChanged(category);
            if (hasChanged) {
                categoriesToUpdate.push(category);
            }
        }

        for (const category of categoriesToDelete) {
            await indexedDBManager.deleteCategory(category.id);
        }

        for (const category of categoriesToUpdate) {
            await indexedDBManager.storeCategories([category]);
        }
    }

    private async syncFiltersIncremental(
        fetchedFilters: FilterListInterface[],
    ): Promise<void> {
        const existingFilters = await indexedDBManager.getFilters();
        const fetchedFilterIds = new Set(fetchedFilters.map((p) => p.id));

        const filtersToDelete = existingFilters.filter(
            (p) => !fetchedFilterIds.has(p.id),
        );

        const filtersToUpdate: FilterListInterface[] = [];
        for (const filter of fetchedFilters) {
            const hasChanged = await indexedDBManager.hasFilterChanged(filter);
            if (hasChanged) {
                filtersToUpdate.push(filter);
            }
        }

        for (const filter of filtersToDelete) {
            await indexedDBManager.deleteFilter(filter.id);
        }

        for (const product of filtersToUpdate) {
            await indexedDBManager.storeFilters([product]);
        }
    }

    private async syncBrandingIncremental(
        fetchedBranding: BrandingInterface[],
    ): Promise<void> {
        const existingBranding = await indexedDBManager.getBranding();
        const fetchedBrandingIds = new Set(fetchedBranding.map((b) => b.id));

        const brandingToDelete = existingBranding.filter(
            (b) => !fetchedBrandingIds.has(b.id),
        );

        const brandingToUpdate: BrandingInterface[] = [];
        for (const branding of fetchedBranding) {
            const hasChanged =
                await indexedDBManager.hasBrandingChanged(branding);
            if (hasChanged) {
                brandingToUpdate.push(branding);
            }
        }

        for (const branding of brandingToDelete) {
            await indexedDBManager.deleteBranding(branding.id);
        }

        for (const branding of brandingToUpdate) {
            await indexedDBManager.storeBranding([branding]);
        }
    }

    private async fetchAllProducts(): Promise<ProductInterface[]> {
        const allProducts: ProductInterface[] = [];
        let currentPage = 1;
        let hasMorePages = true;

        while (hasMorePages) {
            try {
                const result = await store
                    .dispatch(
                        digitalSalesApiSlice.endpoints.getProducts.initiate(
                            { page: currentPage },
                            { forceRefetch: true },
                        ),
                    )
                    .unwrap();

                allProducts.push(...result.products);

                if (result.meta.current_page < result.meta.last_page) {
                    currentPage++;
                    continue;
                }

                hasMorePages = false;
            } catch (error: unknown) {
                const apiError = error as {
                    status?: number;
                    data?: { status?: number };
                };
                console.error(`Error fetching page ${currentPage}:`, error);
                if (
                    apiError?.status === 401 ||
                    apiError?.data?.status === 401
                ) {
                    throw error;
                }
                hasMorePages = false;
            }
        }

        return allProducts;
    }

    private async fetchProductCategories(
        productIds: number[],
    ): Promise<CategoryInterface[]> {
        if (productIds.length === 0) return [];

        try {
            return await store
                .dispatch(
                    digitalSalesApiSlice.endpoints.getProductCategory.initiate(
                        { product_ids: productIds },
                        { forceRefetch: true },
                    ),
                )
                .unwrap();
        } catch (error: unknown) {
            const apiError = error as {
                status?: number;
                data?: { status?: number };
            };
            console.error("Error fetching categories:", error);
            if (apiError?.status === 401 || apiError?.data?.status === 401) {
                throw error;
            }
            return [];
        }
    }

    private async fetchAllFilters(): Promise<FilterListInterface[]> {
        const allFilters: FilterListInterface[] = [];

        try {
            const result = await store
                .dispatch(
                    digitalSalesApiSlice.endpoints.getFilters.initiate(
                        undefined,
                        { forceRefetch: true },
                    ),
                )
                .unwrap();
            if (result.length > 0) {
                allFilters.push(...result);
            }
        } catch (error: unknown) {
            const apiError = error as {
                status?: number;
                data?: { status?: number };
            };
            console.error("Error fetching filters:", error);
            if (apiError?.status === 401 || apiError?.data?.status === 401) {
                throw error;
            }
        }

        return allFilters;
    }

    private async fetchBranding(type: string): Promise<BrandingInterface[]> {
        const allBranding: BrandingInterface[] = [];

        try {
            const result = await store
                .dispatch(
                    digitalSalesApiSlice.endpoints.getBranding.initiate(
                        { type: type },
                        { forceRefetch: true },
                    ),
                )
                .unwrap();

            if (result.length > 0) {
                allBranding.push(...result);
            }
        } catch (error: unknown) {
            const apiError = error as {
                status?: number;
                data?: { status?: number };
            };
            console.error("Error fetching branding:", error);
            if (apiError?.status === 401 || apiError?.data?.status === 401) {
                throw error;
            }
        }

        return allBranding;
    }

    private async downloadAllImages(
        products: ProductInterface[],
        onProgress?: (progress: SyncProgress) => void,
    ): Promise<void> {
        const currentImageUrls: string[] = [];
        products.forEach((product) => {
            product.productImages?.forEach((image) => {
                if (
                    image?.converted?.HD &&
                    !currentImageUrls.includes(image?.converted?.HD)
                ) {
                    currentImageUrls.push(image?.converted?.HD);
                }
            });
        });

        const allStoredImages = await this.getAllStoredImages();
        const storedImageUrls = allStoredImages.map((img) => img.url);

        const imagesToDownload = currentImageUrls.filter(
            (url) => !storedImageUrls.includes(url),
        );

        const orphanedImages = storedImageUrls.filter(
            (url) => !currentImageUrls.includes(url),
        );

        for (const url of orphanedImages) {
            await indexedDBManager.deleteImage(url);
        }

        let processed = 0;
        const totalImages = imagesToDownload.length;

        if (totalImages === 0) {
            onProgress?.({
                stage: "downloading-images",
                progress: 90,
                total: 100,
                message: `No new images to download. Cleaned up ${orphanedImages.length} orphaned images.`,
            });
            return;
        }

        const batchSize = 5;
        for (let i = 0; i < imagesToDownload.length; i += batchSize) {
            const batch = imagesToDownload.slice(i, i + batchSize);

            await Promise.all(
                batch.map(async (url) => {
                    try {
                        await this.downloadAndStoreImage(url);
                    } catch (error) {
                        console.warn(`Failed to download image ${url}:`, error);
                    } finally {
                        processed++;

                        // Update progress for image downloads (40% to 90%)
                        const imageProgress = (processed / totalImages) * 50; // 50% of total progress allocated to images
                        onProgress?.({
                            stage: "downloading-images",
                            progress: Math.round(40 + imageProgress),
                            total: 100,
                            message: `Downloading images... ${processed}/${totalImages} (cleaned up ${orphanedImages.length} orphaned)`,
                        });
                    }
                }),
            );
        }
    }

    private async downloadAllBrandingFiles(
        branding: BrandingInterface[],
        onProgress?: (progress: SyncProgress) => void,
    ): Promise<void> {
        const currentBrandingUrls: string[] = [];
        branding.forEach((brandingItem) => {
            if (
                brandingItem.mainUrl &&
                !currentBrandingUrls.includes(brandingItem.mainUrl)
            ) {
                currentBrandingUrls.push(brandingItem.mainUrl);
            }
            if (
                brandingItem.thumbnailUrl &&
                !currentBrandingUrls.includes(brandingItem.thumbnailUrl)
            ) {
                currentBrandingUrls.push(brandingItem.thumbnailUrl);
            }
        });

        const allStoredBrandingFiles = await this.getAllStoredBrandingFiles();
        const storedBrandingUrls = allStoredBrandingFiles.map(
            (file) => file.url,
        );

        const brandingFilesToDownload = currentBrandingUrls.filter(
            (url) => !storedBrandingUrls.includes(url),
        );

        const orphanedBrandingFiles = storedBrandingUrls.filter(
            (url) => !currentBrandingUrls.includes(url),
        );

        for (const url of orphanedBrandingFiles) {
            await indexedDBManager.deleteBrandingFile(url);
        }

        let processed = 0;
        const totalFiles = brandingFilesToDownload.length;

        if (totalFiles === 0) {
            onProgress?.({
                stage: "downloading-branding-files",
                progress: 90,
                total: 100,
                message: `No new branding files to download. Cleaned up ${orphanedBrandingFiles.length} orphaned files.`,
            });
            return;
        }

        const batchSize = 3;
        for (let i = 0; i < brandingFilesToDownload.length; i += batchSize) {
            const batch = brandingFilesToDownload.slice(i, i + batchSize);

            await Promise.all(
                batch.map(async (url) => {
                    try {
                        await this.downloadAndStoreBrandingFile(url);
                    } catch (error) {
                        console.warn(
                            `Failed to download branding file ${url}:`,
                            error,
                        );
                    } finally {
                        processed++;

                        // Update progress for branding file downloads (90% to 95%)
                        const fileProgress = (processed / totalFiles) * 5; // 5% of total progress allocated to branding files
                        onProgress?.({
                            stage: "downloading-branding-files",
                            progress: Math.round(90 + fileProgress),
                            total: 100,
                            message: `Downloading branding files... ${processed}/${totalFiles} (cleaned up ${orphanedBrandingFiles.length} orphaned)`,
                        });
                    }
                }),
            );
        }
    }

    private async getAllStoredImages(): Promise<{ url: string }[]> {
        await indexedDBManager.init();

        return new Promise((resolve, reject) => {
            if (!indexedDBManager["db"]) {
                reject(new Error("Database not initialized"));
                return;
            }

            const transaction = indexedDBManager["db"].transaction(
                ["images"],
                "readonly",
            );
            const objectStore = transaction.objectStore("images");
            const request = objectStore.getAllKeys();

            request.onsuccess = () => {
                const urls = request.result.map((key) => ({
                    url: key as string,
                }));
                resolve(urls);
            };
            request.onerror = () => reject(request.error);
        });
    }

    private async getAllStoredBrandingFiles(): Promise<{ url: string }[]> {
        await indexedDBManager.init();

        return new Promise((resolve, reject) => {
            if (!indexedDBManager["db"]) {
                reject(new Error("Database not initialized"));
                return;
            }

            const transaction = indexedDBManager["db"].transaction(
                ["brandingFiles"],
                "readonly",
            );
            const objectStore = transaction.objectStore("brandingFiles");
            const request = objectStore.getAllKeys();

            request.onsuccess = () => {
                const urls = request.result.map((key) => ({
                    url: key as string,
                }));
                resolve(urls);
            };
            request.onerror = () => reject(request.error);
        });
    }

    private async downloadAndStoreImage(url: string): Promise<void> {
        try {
            const existingImage = await indexedDBManager.getImage(url);
            if (existingImage) {
                return;
            }

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            await indexedDBManager.storeImage(url, blob);
        } catch (error) {
            console.error(`Failed to download image ${url}:`, error);
            throw error;
        }
    }

    private async downloadAndStoreBrandingFile(url: string): Promise<void> {
        try {
            const existingFile = await indexedDBManager.getBrandingFile(url);
            if (existingFile) {
                return;
            }

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            await indexedDBManager.storeBrandingFile(url, blob);
        } catch (error) {
            console.error(`Failed to download branding file ${url}:`, error);
            throw error;
        }
    }

    async getLocalData(): Promise<{
        products: ProductInterface[];
        filters: FilterListInterface[];
        branding: BrandingInterface[];
        categories: CategoryInterface[];
        lastSync: number | null;
    }> {
        await indexedDBManager.init();

        const [products, filters, branding, categories, lastSyncRaw] =
            await Promise.all([
                indexedDBManager.getProducts(),
                indexedDBManager.getFilters(),
                indexedDBManager.getBranding(),
                indexedDBManager.getCategories(),
                indexedDBManager.getMetadata("lastSync"),
            ]);

        const lastSync = typeof lastSyncRaw === "number" ? lastSyncRaw : null;

        return {
            products,
            filters,
            branding,
            categories,
            lastSync,
        };
    }

    async getLocalImageUrl(url: string): Promise<string | null> {
        if (!url) {
            return null;
        }

        try {
            await indexedDBManager.init();
            const storedImage = await indexedDBManager.getImage(url);

            if (storedImage) {
                return URL.createObjectURL(storedImage.blob);
            }

            return null;
        } catch (error) {
            console.error(`Failed to get local image ${url}:`, error);
            return null;
        }
    }

    async getLocalBrandingFileUrl(url: string): Promise<string | null> {
        if (!url) {
            return null;
        }

        try {
            await indexedDBManager.init();
            const storedFile = await indexedDBManager.getBrandingFile(url);

            if (storedFile) {
                return URL.createObjectURL(storedFile.blob);
            }

            return null;
        } catch (error) {
            console.error(`Failed to get local branding file ${url}:`, error);
            return null;
        }
    }

    async clearAllData(): Promise<void> {
        await indexedDBManager.init();
        await indexedDBManager.clearAll();
    }

    async hasSynchronizedData(): Promise<boolean> {
        try {
            await indexedDBManager.init();

            const [products, lastSync] = await Promise.all([
                indexedDBManager.getProducts(),
                indexedDBManager.getMetadata("lastSync"),
            ]);

            return (
                products.length > 0 &&
                lastSync !== null &&
                typeof lastSync === "number"
            );
        } catch (error) {
            console.error("Error checking synchronized data:", error);
            return false;
        }
    }

    async getProductById(
        id: number,
    ): Promise<ProductDetailsDataInterface | null> {
        try {
            await indexedDBManager.init();
            return await indexedDBManager.getProductById(id);
        } catch (error) {
            console.error(`Failed to get product with ID ${id}:`, error);
            return null;
        }
    }
}

export const dataSyncService = new DataSyncService();
